import {
  useState,
  useMemo,
  MouseEvent,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { ACCREDITED_CONTACTS_QUERY_KEY } from '../../../constants';
import {
  UserRole,
  getProjectType,
  ProjectTypes,
  IMPLICIT_NOMINATED_FOR_DINNER_ROLES,
  ACCREDITATION_ROLES,
} from '@r-and-a-shared-ui/utils';
import { ColumnIds, DEFAULT_ROWS_PER_PAGE } from '../constants';
import { Order, DataGridTableProps } from '../types';
import { useCommonUIActionsContext } from '../../../contexts/CommonUIActionsContext';
import useFetchCurrentUserData from '../../../hooks/useFetchCurrentUserData';
import useFetchAccreditationFormData from '../../../hooks/useFetchAccreditationFormData';
import { getComparator, normalizeRows } from '../helpers';
import {
  getPPContactToDisplay,
  parseUserRole,
} from '../../../helpers/PlayerPortals';

export const useDataGridTable = ({
  columns,
  rows = [],
  defaultSortColumn,
  defaultSortDirection,
  defaultRowsPerPage,
  callbackUrl,
  sidePanel,
  customContactId,
  playersTableLabel,
  managersTableLabel,
  accreditationGridTable,
  kontentItemId,
}: DataGridTableProps) => {
  const projectType = getProjectType();
  const isPPproject = projectType === ProjectTypes.PlayerPortals;

  const isAccreditationGridTable = !!accreditationGridTable;

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(
    defaultRowsPerPage ? Number(defaultRowsPerPage) : DEFAULT_ROWS_PER_PAGE,
  );
  const [fetchedRows, setFetchedRows] = useState<any[]>(rows);
  const [hasFetched, setHasFetched] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [order, setOrder] = useState<Order>(defaultSortDirection);
  const [orderBy, setOrderBy] = useState<string | null>(null);

  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any | null>(null);

  const context = useCommonUIActionsContext();

  const { userDataAPI } = context || {};
  const mappedPPContact = getPPContactToDisplay();
  const mappedPPContactRole = parseUserRole(mappedPPContact?.role);

  const isManagersTable =
    (mappedPPContactRole === UserRole.Manager ||
      mappedPPContactRole === UserRole.LCQManager) &&
    !customContactId;

  const title = isManagersTable ? managersTableLabel : playersTableLabel;

  const fetchResponse = accreditationGridTable
    ? useFetchAccreditationFormData(
        userDataAPI,
        accreditationGridTable.selectedPlayerId || '',
        callbackUrl,
      )
    : useFetchCurrentUserData(
        callbackUrl,
        userDataAPI,
        ACCREDITED_CONTACTS_QUERY_KEY,
        customContactId ?? mappedPPContact?.id,
      );

  const {
    data,
    loading,
    hasFetched: responseHasFetched = false,
  } = fetchResponse;

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (defaultSortColumn?.id) {
      setOrderBy(defaultSortColumn.id);
    }
  }, [defaultSortColumn]);

  useEffect(() => {
    if (!data) return;
    setFetchedRows(normalizeRows(data.items || [], columns));
  }, [data, columns]);

  useEffect(() => {
    if (loading) {
      setHasFetched(false);
    } else {
      setHasFetched(responseHasFetched);
    }
  }, [loading, responseHasFetched]);

  const filteredRows = useMemo(() => {
    if (!searchQuery || searchQuery.trim() === '') return fetchedRows;

    const query = searchQuery.toLowerCase();
    const nameColumn = columns.find(col => col.id === ColumnIds.Name);

    if (nameColumn) {
      return fetchedRows.filter(row =>
        String(row[nameColumn.id] ?? '')
          .toLowerCase()
          .includes(query),
      );
    }

    return fetchedRows.filter(row =>
      columns.some(col =>
        String(row[col.id] ?? '')
          .toLowerCase()
          .includes(query),
      ),
    );
  }, [searchQuery, fetchedRows, columns]);

  const visibleRows = useMemo(() => {
    const sortedRows = [...filteredRows].sort(
      orderBy ? getComparator(order, orderBy) : () => 0,
    );

    if (isAccreditationGridTable) {
      return sortedRows;
    }

    return sortedRows.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );
  }, [
    order,
    orderBy,
    page,
    rowsPerPage,
    filteredRows,
    isAccreditationGridTable,
  ]);

  const emptyRows = useMemo(() => {
    if (isAccreditationGridTable || filteredRows.length === 0) return 0;
    return Math.max(0, (1 + page) * rowsPerPage - filteredRows.length);
  }, [page, rowsPerPage, filteredRows, isAccreditationGridTable]);

  const isSortDisabled = useMemo(
    () => filteredRows.length === 0,
    [filteredRows],
  );

  const currentNominatedForDinnerCount = useMemo(() => {
    if (!isAccreditationGridTable) {
      return 0;
    }

    return fetchedRows.filter(row => {
      const role = row?.[ColumnIds.SubCategory];

      if (
        typeof role === 'string' &&
        IMPLICIT_NOMINATED_FOR_DINNER_ROLES.includes(
          Object.values(ACCREDITATION_ROLES).find(r => r.label === role)
            ?.value!,
        )
      ) {
        return false;
      }
      const value = row?.[ColumnIds.NominatedForDinner];

      if (typeof value === 'boolean') {
        return value;
      }

      if (typeof value === 'string') {
        return value.toLowerCase() === 'true';
      }

      return false;
    }).length;
  }, [fetchedRows, isAccreditationGridTable]);

  const currentApplicationsPerRoleCount = useMemo(() => {
    if (!isAccreditationGridTable) {
      return {};
    }

    return fetchedRows.reduce((acc: Record<string, number>, row) => {
      const role = row?.[ColumnIds.SubCategory];

      if (typeof role === 'string' && role.trim().length > 0) {
        acc[role] = (acc[role] ?? 0) + 1;
      }

      return acc;
    }, {});
  }, [fetchedRows, isAccreditationGridTable]);

  const filteredIds = useMemo(
    () => new Set(filteredRows.map(row => row.id)),
    [filteredRows],
  );

  const filteredItems = useMemo(() => {
    if (!data?.items) return [];
    return data.items.filter((item: any) => filteredIds.has(item.id));
  }, [data?.items, filteredIds]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleRequestSort = useCallback(
    (event: React.MouseEvent<unknown>, property: string) => {
      setOrder(prev =>
        orderBy === property && prev === 'asc' ? 'desc' : 'asc',
      );
      setOrderBy(property);
    },
    [orderBy],
  );

  const handleChangePage = useCallback((_: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setTimeout(() => {
      if (wrapperRef.current) {
        wrapperRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }, 300);
  };

  const handleRowClick = useCallback(
    (_event: MouseEvent<unknown>, id: string) => {
      if (isManagersTable && sidePanel) {
        setSelectedItem(
          filteredItems.find((row: any) => row.id === id) || null,
        );
        setIsPanelOpen(true);
      }
    },
    [filteredItems, isManagersTable, sidePanel],
  );

  const handlePlayerChange = useCallback(
    (selectedPlayerId: string) => {
      if (
        accreditationGridTable &&
        accreditationGridTable?.setSelectedPlayerId
      ) {
        accreditationGridTable.setSelectedPlayerId(selectedPlayerId);
      }
    },
    [accreditationGridTable],
  );

  const handleAccreditationFormClosed = useCallback(() => {
    if (!accreditationGridTable) {
      return;
    }

    const refetch = (fetchResponse as { refetch?: () => Promise<void> })
      .refetch;
    refetch?.();
  }, [accreditationGridTable, fetchResponse]);

  return {
    isAccreditationGridTable,
    accreditationGridTable,
    isPPproject,
    title,
    columns,
    sidePanel,
    isManagersTable,
    loading,
    hasFetched,
    order,
    orderBy,
    searchQuery,
    isSortDisabled,
    currentNominatedForDinnerCount,
    currentApplicationsPerRoleCount,
    page,
    rowsPerPage,
    filteredRows,
    visibleRows,
    emptyRows,
    filteredItems,
    isPanelOpen,
    selectedItem,
    wrapperRef,
    setIsPanelOpen,
    setSelectedItem,
    handleSearchChange,
    handleRequestSort,
    handleChangePage,
    handleChangeRowsPerPage,
    handleRowClick,
    handlePlayerChange,
    handleAccreditationFormClosed,
    kontentItemId,
  };
};
